// Măsurare rapidă cu rotița mouse-ului.
//
// Ține apăsat butonul din mijloc și trage: apare o zonă cu procentul de mișcare,
// diferența de preț și numărul de bare — exact ca la ScalpX (și ca la TradingView).
// La eliberare dispare. Nu lasă nimic în urmă și nu se salvează.

import { el, fmtPrice, priceDigits, TF_SECONDS, fmtDuration } from '../core/util.js';

const UP = '#16C784';
const DOWN = '#F6455F';

export class QuickMeasure {
  /**
   * @param {Chart} chart  instanța noastră de grafic
   * @param {HTMLElement} host  containerul panoului principal
   */
  constructor(chart, host) {
    this.chart = chart;
    this.host = host;
    this.active = null;

    this.canvas = el('canvas', { class: 'chart-measure-layer' });
    this.ctx = this.canvas.getContext('2d');
    this.host.append(this.canvas);

    this.resizeObserver = new ResizeObserver(() => this.#resize());
    this.resizeObserver.observe(this.host);
    this.#resize();
    this.#bind();
  }

  #resize() {
    const dpr = window.devicePixelRatio || 1;
    const w = this.host.clientWidth;
    const h = this.host.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.render();
  }

  #point(event) {
    const rect = this.canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    // Prin `Chart`, ca măsurarea să meargă și în zona goală din dreapta graficului.
    const market = this.chart.toMarket(x, y);
    return market ? { x, y, ...market } : null;
  }

  #bind() {
    this.onDown = (event) => {
      if (event.button !== 1) return;          // doar butonul din mijloc
      const start = this.#point(event);
      if (!start) return;
      event.preventDefault();
      this.active = { start, end: start };
      this.render();
    };

    this.onMove = (event) => {
      if (!this.active) return;
      const end = this.#point(event);
      if (end) { this.active.end = end; this.render(); }
    };

    this.onUp = (event) => {
      if (!this.active || event.button !== 1) return;
      this.active = null;
      this.render();
    };

    // `auxclick` deschide meniul de scroll în unele browsere — îl blocăm cât măsurăm.
    this.onAux = (event) => { if (event.button === 1) event.preventDefault(); };

    this.host.addEventListener('mousedown', this.onDown);
    this.host.addEventListener('auxclick', this.onAux);
    window.addEventListener('mousemove', this.onMove);
    window.addEventListener('mouseup', this.onUp);
  }

  /** Oprește o măsurare în curs — de exemplu când se schimbă moneda. */
  cancel() {
    this.active = null;
    this.render();
  }

  render() {
    const ctx = this.ctx;
    if (!ctx) return;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    if (!this.active) return;

    const { start, end } = this.active;
    const up = end.price >= start.price;
    const color = up ? UP : DOWN;
    const x = Math.min(start.x, end.x);
    const y = Math.min(start.y, end.y);
    const w = Math.abs(end.x - start.x);
    const h = Math.abs(end.y - start.y);

    ctx.save();
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.14;
    ctx.fillRect(x, y, w, h);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 3]);
    ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);

    // Săgeata verticală, de la prețul de start la cel de final.
    const midX = start.x + (end.x - start.x) / 2;
    ctx.beginPath();
    ctx.moveTo(midX, start.y);
    ctx.lineTo(midX, end.y);
    ctx.stroke();
    const dir = up ? -1 : 1;
    ctx.beginPath();
    ctx.moveTo(midX, end.y);
    ctx.lineTo(midX - 4, end.y + dir * 7);
    ctx.lineTo(midX + 4, end.y + dir * 7);
    ctx.closePath();
    ctx.fill();

    const pct = start.price ? ((end.price - start.price) / start.price) * 100 : 0;
    const digits = priceDigits(start.price);
    const step = TF_SECONDS[this.chart.tf] || 300;
    const bars = Math.round(Math.abs(end.time - start.time) / step);

    this.#label(midX, y + h / 2, [
      `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`,
      `${end.price >= start.price ? '+' : '−'}${fmtPrice(Math.abs(end.price - start.price), digits)}`,
      `${bars} бар · ${fmtDuration(bars * step)}`,
    ], color);
    ctx.restore();
  }

  #label(cx, cy, lines, color) {
    const ctx = this.ctx;
    ctx.save();
    ctx.font = '11px Manrope, sans-serif';
    const width = Math.max(...lines.map((t) => ctx.measureText(t).width)) + 16;
    const height = lines.length * 15 + 10;
    const x = cx - width / 2;
    const y = cy - height / 2;

    ctx.globalAlpha = 0.95;
    ctx.fillStyle = '#0E1220';
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.roundRect(x, y, width, height, 6);
    ctx.fill();
    ctx.stroke();

    ctx.globalAlpha = 1;
    ctx.textAlign = 'center';
    lines.forEach((text, i) => {
      ctx.fillStyle = i === 0 ? color : '#9FAEC4';
      ctx.font = i === 0 ? '700 12px Manrope, sans-serif' : '11px Manrope, sans-serif';
      ctx.fillText(text, cx, y + 18 + i * 15);
    });
    ctx.restore();
  }

  destroy() {
    this.host.removeEventListener('mousedown', this.onDown);
    this.host.removeEventListener('auxclick', this.onAux);
    window.removeEventListener('mousemove', this.onMove);
    window.removeEventListener('mouseup', this.onUp);
    this.resizeObserver?.disconnect();
    this.canvas.remove();
  }
}
